import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  getReconciliationSnapshot,
  importBankStatementLines,
  resolveReconciliationFinding,
  runL1Reconciliation,
  runL2Reconciliation,
  runL3Reconciliation,
  runL4Reconciliation,
  updateEInvoiceAck,
} from './reconciliation-api';
import type {
  ImportBankStatementLinesPayload,
  ReconciliationSnapshot,
  ResolveReconciliationFindingPayload,
  RunL1ReconciliationPayload,
  UpdateEInvoiceAckPayload,
} from './types';

export const reconciliationKeys = {
  all: ['reconciliation'] as const,
  snapshot: () => [...reconciliationKeys.all, 'snapshot'] as const,
};

export function useReconciliationSnapshot() {
  return useQuery<ReconciliationSnapshot>({
    queryKey: reconciliationKeys.snapshot(),
    queryFn: getReconciliationSnapshot,
    staleTime: 30_000,
  });
}

export function useRunL1Reconciliation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: RunL1ReconciliationPayload) => runL1Reconciliation(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reconciliationKeys.snapshot() });
    },
  });
}

export function useRunL2Reconciliation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: RunL1ReconciliationPayload) => runL2Reconciliation(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reconciliationKeys.snapshot() });
    },
  });
}

export function useRunL3Reconciliation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: RunL1ReconciliationPayload) => runL3Reconciliation(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reconciliationKeys.snapshot() });
    },
  });
}

export function useRunL4Reconciliation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: RunL1ReconciliationPayload) => runL4Reconciliation(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reconciliationKeys.snapshot() });
    },
  });
}

export function useImportBankStatementLines() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ImportBankStatementLinesPayload) => importBankStatementLines(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reconciliationKeys.snapshot() });
    },
  });
}

export function useUpdateEInvoiceAck() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateEInvoiceAckPayload) => updateEInvoiceAck(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reconciliationKeys.snapshot() });
    },
  });
}

export function useResolveReconciliationFinding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      findingId,
      payload,
    }: {
      findingId: string;
      payload: ResolveReconciliationFindingPayload;
    }) => resolveReconciliationFinding(findingId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reconciliationKeys.snapshot() });
    },
  });
}
